import { THEME_COLORS } from "@common/styles";
import { AntDesign, MaterialCommunityIcons } from "@expo/vector-icons";
import { CircularProgress, Slider } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";

interface PropTypes {
  url: string;
}

const VideoPlayer = ({ url }: PropTypes) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [rotation, setRotation] = useState<number>(0);
  const [randomReload, setRandomReload] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>();

  const downloadOnPress = async () => {
    const urlSplit = url?.split?.("/");
    const fileName = urlSplit?.[urlSplit?.length - 1];
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.download = fileName;
    link.click();
  };

  const playOnPress = () => {
    videoRef.current?.play?.();
    setIsVideoPlaying(true);
  };

  const pauseOnPress = () => {
    videoRef.current?.pause?.();
    setIsVideoPlaying(false);
  };

  const onSeekChange = (_, value) => {
    if (videoRef.current) {
      videoRef.current.currentTime = value;
      setRandomReload(!randomReload);
    }
  };

  const rotateOnPress = () => {
    setRotation((rotation % 360) + 90);
  };

  const onLoadStart = () => {
    setLoading(true);
  };

  const onLoadedData = () => {
    videoRef.current.onloadeddata = () => {
      setLoading(false);
    };
  };

  useEffect(() => {
    if (isVideoPlaying) {
      if (videoRef.current?.currentTime === videoRef.current?.duration) {
        setIsVideoPlaying(false);
      }
      const interval = setInterval(() => {
        setRandomReload(!randomReload);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isVideoPlaying, randomReload]);

  useEffect(() => {
    onLoadedData?.();
  }, []);

  return (
    <View style={styles?.container}>
      <video
        ref={videoRef}
        onLoadStart={onLoadStart}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          rotate: `${rotation}deg`,
        }}
        src={url}
      />
      <View style={styles?.controlsView}>
        <TouchableOpacity style={styles?.playTO} onPress={isVideoPlaying ? pauseOnPress : playOnPress}>
          <AntDesign name={isVideoPlaying ? "pause-circle" : "play-circle"} size={30} color={THEME_COLORS?.white} />
        </TouchableOpacity>
        <Slider
          value={videoRef.current?.currentTime ?? 0}
          max={videoRef.current?.duration ?? 0}
          onChange={onSeekChange}
        />
        <TouchableOpacity style={styles?.rotateTO} onPress={rotateOnPress}>
          <MaterialCommunityIcons name="rotate-right" size={25} color={THEME_COLORS.white} />
        </TouchableOpacity>
        <TouchableOpacity style={styles?.downloadTO} onPress={downloadOnPress}>
          <MaterialCommunityIcons name="download-box-outline" size={25} color={THEME_COLORS.white} />
        </TouchableOpacity>
      </View>
      {loading ? (
        <View style={styles?.loadingView}>
          <CircularProgress />
        </View>
      ) : null}
    </View>
  );
};

export default VideoPlayer;

const styles = StyleSheet?.create?.({
  container: { flex: 1 },
  controlsView: {
    position: "absolute",
    flexDirection: "row",
    padding: 10,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#0000005F",
    alignItems: "center",
  },
  playTO: {
    width: 30,
    height: 30,
    marginEnd: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  rotateTO: {
    width: 20,
    height: 20,
    marginStart: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingView: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  downloadTO: {
    width: 20,
    height: 20,
    marginStart: 20,
    alignItems: "center",
    justifyContent: "center",
  },
});
